// API Key Service - Centralized API key management for frontend
// This service handles retrieving and caching API keys from the backend

interface ApiKeyResponse {
  success: boolean;
  apiKey?: string;
  stage?: string;
  timestamp?: number;
  error?: string;
  message?: string;
}

class ApiKeyService {
  private cachedApiKey: string | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

  /**
   * Get API key with caching
   * Retrieves API key from backend and caches it for performance
   */
  async getApiKey(): Promise<string> {
    // Check if we have a valid cached API key
    if (this.cachedApiKey && this.isCacheValid()) {
      console.log('✅ Using cached API key');
      return this.cachedApiKey;
    }

    // Fetch fresh API key from backend
    try {
      console.log('🔑 Fetching API key from backend...');
      
      const apiUrl = process.env.REACT_APP_API_URL;
      const midwayToken = localStorage.getItem('midway_auth_token');
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      if (midwayToken) {
        headers['Authorization'] = `Bearer ${midwayToken}`;
      }
      
      const response = await fetch(`${apiUrl}/api/get-api-key`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch API key: ${response.status} ${response.statusText}`);
      }

      const data: ApiKeyResponse = await response.json();

      if (!data.success || !data.apiKey) {
        throw new Error(data.message || 'Failed to retrieve API key');
      }

      // Cache the API key
      this.cachedApiKey = data.apiKey;
      this.cacheTimestamp = Date.now();

      console.log('✅ API key retrieved and cached successfully');
      return data.apiKey;

    } catch (error) {
      console.error('❌ Failed to retrieve API key:', error);
      throw new Error(`API key retrieval failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get API key headers for authenticated requests
   * Returns headers object with API key and Midway token included
   */
  async getApiKeyHeaders(): Promise<Record<string, string>> {
    console.log('🔑 [API-KEY-SERVICE] Getting API key headers...');
    
    const apiKey = await this.getApiKey();
    const midwayToken = localStorage.getItem('midway_auth_token');
    
    console.log('🔍 [API-KEY-SERVICE] Token check:', {
      hasMidwayToken: !!midwayToken,
      tokenLength: midwayToken?.length || 0,
      tokenPreview: midwayToken ? `${midwayToken.substring(0, 20)}...` : 'null'
    });
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'x-api-key': apiKey
    };
    
    // Add Midway token if available
    if (midwayToken) {
      headers['Authorization'] = `Bearer ${midwayToken}`;
      console.log('✅ [API-KEY-SERVICE] Authorization header added');
    } else {
      console.warn('⚠️ [API-KEY-SERVICE] No Midway token in localStorage - Authorization header NOT added');
    }
    
    console.log('📦 [API-KEY-SERVICE] Final headers:', {
      hasContentType: !!headers['Content-Type'],
      hasApiKey: !!headers['x-api-key'],
      hasAuthorization: !!headers['Authorization']
    });
    
    return headers;
  }

  /**
   * Clear cached API key
   * Forces next request to fetch fresh API key
   */
  clearCache(): void {
    this.cachedApiKey = null;
    this.cacheTimestamp = 0;
    console.log('🗑️ API key cache cleared');
  }

  /**
   * Check if cached API key is still valid
   */
  private isCacheValid(): boolean {
    const now = Date.now();
    return (now - this.cacheTimestamp) < this.CACHE_DURATION;
  }

  /**
   * Get cache status for debugging
   */
  getCacheStatus(): { cached: boolean; age: number; valid: boolean } {
    const now = Date.now();
    const age = now - this.cacheTimestamp;
    
    return {
      cached: this.cachedApiKey !== null,
      age: age,
      valid: this.isCacheValid()
    };
  }
}

// Export singleton instance
export const apiKeyService = new ApiKeyService();

// Export class for testing
export default ApiKeyService;
